import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, Mail, Moon, AlertTriangle, Shield } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { SiteSettings } from "@/lib/types";
import { supabase } from "@/integrations/supabase/client";

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const navigate = useNavigate();
  const { signIn, signUp, user } = useAuth();

  // Fetch site settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('site_settings')
          .select('*')
          .single();

        if (!error && data) {
          setRegistrationEnabled(data.registration_enabled);
        }
      } catch (err) {
        console.error('Error fetching site settings:', err);
      } finally {
        setSettingsLoaded(true);
      }
    };

    fetchSettings();
  }, []);

  // Redirect if already authenticated
  if (user) {
    return <Navigate to="/dashboard" />;
  }

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate inputs
    if (!email || !password) {
      toast.error("Please enter both email and password");
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    // Basic password strength check
    if (isSignUp && password.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }
    
    setLoading(true);

    try {
      if (isSignUp) {
        if (!registrationEnabled) {
          toast.error("Registration is currently disabled by the administrator");
          setLoading(false);
          return;
        }
        
        const { error, success } = await signUp(email, password);
        if (success) {
          toast.success("Account created! Please check your email for verification.");
          setIsSignUp(false);
        } else {
          toast.error(error.message || "Failed to create account");
        }
      } else {
        const { error, success } = await signIn(email, password);
        if (success) {
          toast.success("Login successful");
          navigate("/dashboard");
        } else {
          toast.error(error.message || "Invalid credentials");
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-background gradient-bg flex flex-col items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <Button variant="ghost" size="icon" className="w-8 h-8 text-white/70 hover:text-white">
          <Moon size={16} />
        </Button>
      </div>
      <div className="w-full max-w-md">
        <Card className="glass-card">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-3xl font-bold text-primary tracking-tighter">nativvshare</CardTitle>
            <p className="text-white/70 font-mono text-sm tracking-tight">
              {isSignUp ? "Create an account" : "Login to your account"}
            </p>
            {isSignUp && !registrationEnabled && settingsLoaded && (
              <div className="mt-2 p-2 bg-destructive/10 rounded-md border border-destructive/30 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <span className="text-destructive text-xs">
                  Registration is currently disabled by the administrator
                </span>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAuth} className="space-y-4">
              <div className="space-y-2">
                <div className="relative">
                  <Input 
                    type="email"
                    placeholder="Email address"
                    className="pl-10 bg-black/30 border-white/10 text-white font-mono text-sm"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    aria-autocomplete="both"
                  />
                  <Mail className="absolute left-3 top-2.5 h-5 w-5 text-primary/70" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="relative">
                  <Input 
                    type="password"
                    placeholder="Password"
                    className="pl-10 bg-black/30 border-white/10 text-white font-mono text-sm"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete={isSignUp ? "new-password" : "current-password"}
                  />
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-primary/70" />
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90 transition-opacity font-mono text-sm tracking-tighter"
                disabled={loading || (isSignUp && !registrationEnabled && settingsLoaded)}
              >
                {loading ? "Processing..." : isSignUp ? "Sign Up" : "Login"}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  className="text-white/70 hover:text-primary text-sm font-mono tracking-tight"
                  onClick={() => setIsSignUp(!isSignUp)}
                >
                  {isSignUp ? "Already have an account? Login" : "Don't have an account? Sign Up"}
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
